"use client";

import { motion, useInView } from "framer-motion";
import Image from "next/image";
import { useRef } from "react";

export default function ProgramsDetailSection() {
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, amount: 0.1 });

	return (
		<section ref={ref} className="py-16 md:py-24 bg-white">
			<div className="container mx-auto px-4">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: -20 }}
					animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
					transition={{ duration: 0.6, ease: "easeOut" }}
					className="text-center mb-12"
				>
					<h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
						Programs Designed To Unlock Your
						<br />
						Full Potential
					</h1>
				</motion.div>

				{/* Main Content */}
				<div className="max-w-6xl mx-auto">
					{/* First Row - Overview, What We Offer, Admission Details + Image */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
						transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
						className="grid md:grid-cols-2 gap-8 mb-8"
					>
						{/* Left Column */}
						<div className="space-y-8">
							{/* Overview */}
							<div>
								<h2 className="text-xl font-bold text-gray-900 mb-3">
									Overview
								</h2>
								<p className="text-sm text-gray-700 leading-relaxed">
									Reliance Future Football Academy (RFFA) is a premier youth
									athletic development institution dedicated to nurturing young
									football talents through a balanced combination of sports
									excellence and academic growth. At RFA, we offer a structured
									youth training program focused on developing strength, speed,
									agility, tactical intelligence, and self-confidence.
								</p>
							</div>

							{/* What We Offer */}
							<div>
								<h2 className="text-xl font-bold text-gray-900 mb-3">
									WHAT WE OFFER
								</h2>
								<ul className="text-sm text-gray-700 space-y-1">
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>Comprehensive Football Training Programs</span>
									</li>
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>Academic Excellence And Career Guidance</span>
									</li>
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>Expert And Professional Coaching</span>
									</li>
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>
											Character Development, Discipline, And Leadership Training
										</span>
									</li>
								</ul>
							</div>

							{/* Admission Details */}
							<div>
								<h2 className="text-xl font-bold text-gray-900 mb-3">
									ADMISSION DETAILS
								</h2>
								<ul className="text-sm text-gray-700 space-y-1">
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>Age Category: 14 - 16 Years</span>
									</li>
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>Requirements</span>
									</li>
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>N50,000 Registration Fee</span>
									</li>
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>N700,000 Annual fee</span>
									</li>
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>Completed Online Registration</span>
									</li>
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>Birth Certificate</span>
									</li>
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>International Passport</span>
									</li>
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>School Records</span>
									</li>
									<li className="flex items-start">
										<span className="mr-2">•</span>
										<span>Medical Certificate</span>
									</li>
								</ul>
								<p className="text-sm text-gray-700 mt-4 leading-relaxed">
									All Admissions Are Subject To Review And Approval By The RFFA
									Management Team. Membership And Participation Are Renewable
									Annually, In Accordance With The Terms And Conditions Outlined
									In The Admission Contract.
								</p>
							</div>
						</div>

						{/* Right Column - Image and Text */}
						<div className="space-y-6">
							{/* Image */}
							<div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden">
								<Image
									src="/assets/pro.png"
									alt="Football training session"
									fill
									className="object-cover"
								/>
							</div>

							{/* Text below image */}
							<p className="text-sm text-gray-700 leading-relaxed">
								The Academy Caters To Youths Between The Ages Of 14- to 16,
								Providing Personalized Coaching, Performance Monitoring, And
								Mentorship To Prepare Them For Opportunities In Both
								Professional Football And Higher Education. Our Programs Are
								Performance-Based And Holistic, Combining Physical Conditioning,
								Technical Drills, Tactical Understanding, And Character
								Education To Produce Well-Rounded Athletes And Responsible
								Individuals.
							</p>

							{/* Training Season */}
							<div>
								<h2 className="text-xl font-bold text-gray-900 mb-2">
									Training Season
								</h2>
								<p className="text-sm text-gray-700 leading-relaxed">
									The Academy&apos;s Operational Calendar Runs From June To May
									Of The Following Year (June-May Annual Session).
								</p>
							</div>
						</div>
					</motion.div>

					{/* Second Row - Two Column Layout */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
						transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
						className="grid md:grid-cols-2 gap-8 mb-8"
					>
						{/* Left Column */}
						<div className="space-y-8">
							{/* Coaching And Leadership */}
							<div>
								<h2 className="text-xl font-bold text-gray-900 mb-3">
									Coaching And Leadership
								</h2>
								<p className="text-sm text-gray-700 leading-relaxed">
									RFFA Is Proud To Have A Team Of Dedicated, Licensed, And
									Highly Experienced Coaches Who Are Certified By The Nigeria
									Football Federation (NFF). Our Leadership And Coaching Staff
									Are Committed To Maintaining The Highest Standards Of
									Training, Mentorship, And Player Welfare. Every Member Of The
									Coaching Team Is Trained Not Only To Develop Football Skills
									But Also To Instill Discipline, Teamwork, Respect, And
									Leadership Qualities In Our Young Athletes.
								</p>
							</div>

							{/* Team Training And Facilities */}
							<div>
								<h2 className="text-xl font-bold text-gray-900 mb-3">
									Team Training And Facilities
								</h2>
								<p className="text-sm text-gray-700 leading-relaxed mb-4">
									Our Training Facility Is Located In Ibadan, Oyo State,
									Nigeria, And Has Been Designed To Provide A Safe, Modern, And
									Motivating Environment For Learning And Athletic Development.
									The RFFA Program Integrates Academic Excellence With
									Professional-Level Football Training, Ensuring That Players
									Grow Intellectually As They Advance Athletically. We Emphasize
									Education-Smart Training, Which Enables Our Players To Balance
									Their Academic Pursuits With Their Sporting Ambitions.
								</p>
								<p className="text-sm text-gray-700 leading-relaxed">
									RFFA Operates On A Non-Discriminatory Policy. We Do Not, And
									Shall Not, Discriminate On The Basis Of Race, Gender,
									Ethnicity, Age, National Origin, Religion, Or Creed In Any Of
									Our Programs, Services, Or Operations. Our Activities Are
									Inclusive And Aimed At Serving The Broader Community With
									Fairness And Integrity.
								</p>
							</div>

							{/* Confidentiality Policy */}
							<div>
								<h2 className="text-xl font-bold text-gray-900 mb-3">
									Confidentiality Policy
								</h2>
								<p className="text-sm text-gray-700 leading-relaxed">
									Reliance Future Football Academy Upholds The Highest Standards
									Of Confidentiality In All Its Operations. All Board Members,
									Staff, And Associates Are Required To Protect The Privacy Of
									The Academy And Its Clients.
								</p>
							</div>
						</div>

						{/* Right Column */}
						<div className="space-y-8">
							{/* Client Confidentiality */}
							<div>
								<h2 className="text-xl font-bold text-gray-900 mb-3">
									Client Confidentiality
								</h2>
								<p className="text-sm text-gray-700 leading-relaxed">
									Players And Their Parents/Guardians Are Also Expected To
									Respect The Academy&apos;s Confidentiality Policy.
									Confidential Information Shared By The Academy Must Not Be
									Disclosed Without Authorization.
								</p>
							</div>

							{/* Conclusion */}
							<div>
								<h2 className="text-xl font-bold text-gray-900 mb-3">
									Conclusion
								</h2>
								<p className="text-sm text-gray-700 leading-relaxed">
									Reliance Future Football Academy Is Committed To Building
									Future Champions Both On And Off The Field. Through A
									Carefully Balanced Combination Of Academic Excellence,
									Professional Training, And Ethical Discipline, We Aim To
									Produce Young Individuals Who Will Represent The Future Of
									Nigerian And Global Football With Pride, Integrity, And Skill.
								</p>
							</div>
						</div>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
